"use client";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AddAnotherReturnModal from "../../components/modals/AddReturnModel";
import CelebrateModal from "../../components/modals/CelebrateModal";
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";
import Navbar from "../../components/account/Navbar";
import { petAPI, postsAPI, userAPI } from "../../services/api";
import Footer from "../../components/account/Footer";
import { useAuth } from "../../hooks/useAuth";
import "./PetStyles.css";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { useFlatpickrUserType } from "../../hooks/useFlatpickrUserType";

const AddPet = () => {
  const { t, i18n } = useTranslation();

  const getLocalizedName = (item) =>
    i18n.language === 'ar'
      ? item?.name_ar || item?.name || item?.name_en
      : item?.name_en || item?.name || item?.name_ar;
  const { width, height } = useWindowSize();
  const navigate = useNavigate();
  const location = useLocation();
  const { userType } = useParams();
  const { getUserType, user } = useAuth();

  // Get user type from auth context (more reliable than URL params)
  const currentUserType = getUserType();

  // Check if user is a Rescuer (subtype check)
  // Fetch from API since subtype might not be in the user object
  const [isRescuer, setIsRescuer] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkRescuerStatus = async () => {
      try {
        // First check localStorage and user object (quick check)
        let rescuerCheck = false;

        // Check localStorage
        try {
          const storedUserData = localStorage.getItem("user_data");
          if (storedUserData) {
            const parsedUser = JSON.parse(storedUserData);
            const subType = parsedUser.sub_type || parsedUser.subType;
            const subTypeDisplay = parsedUser.sub_type_display_name || parsedUser.subTypeDisplayName;

            rescuerCheck = (
              (subType && subType.toLowerCase() === "rescuer") ||
              (subTypeDisplay && subTypeDisplay.toLowerCase() === "rescuer")
            );
          }
        } catch (error) {
          console.error("Error parsing user_data from localStorage:", error);
        }

        // Check user object from useAuth
        if (!rescuerCheck && user) {
          const subType = user.sub_type || user.subType;
          const subTypeDisplay = user.sub_type_display_name || user.subTypeDisplayName;

          rescuerCheck = (
            (subType && subType.toLowerCase() === "rescuer") ||
            (subTypeDisplay && subTypeDisplay.toLowerCase() === "rescuer")
          );
        }

        // If still not found, fetch from API
        if (!rescuerCheck) {
          const response = await userAPI.getProfile();
          const payload = response.data?.data || response.data;

          if (!cancelled && payload) {
            const subType = payload.sub_type || payload.subType;
            const subTypeDisplay = payload.sub_type_display_name || payload.subTypeDisplayName;

            rescuerCheck = (
              (subType && subType.toLowerCase() === "rescuer") ||
              (subTypeDisplay && subTypeDisplay.toLowerCase() === "rescuer")
            );
          }
        }

        if (!cancelled) {
          setIsRescuer(rescuerCheck);
        }
      } catch (error) {
        console.error("Error checking rescuer status:", error);
        if (!cancelled) {
          setIsRescuer(false);
        }
      }
    };

    checkRescuerStatus();

    return () => {
      cancelled = true;
    };
  }, [user]);

  // RTL support
  const isRTL = i18n.language === "ar";

  // User type color configuration for dynamic styling
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8', // Purple
      'VETERINARIAN': '#00D4B3', // Cyan
      'SERVICE_PROVIDER': '#D65745', // Orange
      'CLINIC_MODERATOR': '#2A8AD2', // Blue
      'FIRM': '#1B3A6B', // Navy Blue
    };
    return userTypeConfig[userType] || '#8548A8'; // Default to purple
  };

  // Get the current user type color
  const userTypeColor = getUserTypeColor(currentUserType);

  // Apply user type color to Flatpickr calendars globally
  useFlatpickrUserType();

  // Modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [speciesList, setSpeciesList] = useState([]);
  const [breedList, setBreedList] = useState([]);
  const [selectedSpecies, setSelectedSpecies] = useState("");
  const [showCelebrateModal, setShowCelebrateModal] = useState(false);
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [textPost, setTextPost] = useState("");
  const [formData, setFormData] = useState({
    gender: "",
    adoptedRescued: "adopted", // Default to "adopted"
    petName: "",
    color: "",
    provider: "",
    policyNumber: "",
    species_id: "",
    breed: "",
    spayedNeutered: "",
    microchipId: "",
    smartCollarId: "",
    age: "",
    weight: "",
    adoptionDate: "",
    interests: [],
    petImagePreview: null, // New state for image preview
    petImageFile: null, // New state for image file
  });
  const [isPosting, setIsPosting] = useState(false);
  useEffect(() => {
    petAPI
      .getPetsBySpecies(null, { dropdown: true })
      .then((res) => {
        // If your API returns { data: [...] }
        setSpeciesList(
          Array.isArray(res.data) ? res.data : res.data.data || []
        );
      })
      .catch((err) => {
        console.error(err);
        setSpeciesList([]);
      });
  }, []);

  // Fetch breeds when species changes
  useEffect(() => {
    if (selectedSpecies) {
      petAPI
        .getBreedBySpecies(selectedSpecies, { include_all: true, dropdown: true })
        .then((res) => {
          // If your API returns { data: [...] }
          setBreedList(
            Array.isArray(res.data) ? res.data : res.data.data || []
          );
        })
        .catch((err) => {
          console.error(err);
          setBreedList([]);
        });
    } else {
      setBreedList([]);
    }
  }, [selectedSpecies]);

  // Initialize Flatpickr date pickers
  useEffect(() => {
    const birthDatePicker = flatpickr("#birth-date-input", {
      dateFormat: "Y-m-d",
      maxDate: "today",
      onChange: function (selectedDates, dateStr) {
        setFormData((prev) => ({ ...prev, age: dateStr }));
      }
    });

    const adoptionDatePicker = flatpickr("#adoption-date-input", {
      dateFormat: "Y-m-d",
      maxDate: "today",
      onChange: function (selectedDates, dateStr) {
        setFormData((prev) => ({ ...prev, adoptionDate: dateStr }));
      }
    });

    // Function to close any open Flatpickr calendar on scroll
    const handleScroll = () => {
      if (birthDatePicker.isOpen) {
        birthDatePicker.close();
      }
      if (adoptionDatePicker.isOpen) {
        adoptionDatePicker.close();
      }
    };

    // Add scroll event listeners
    window.addEventListener('scroll', handleScroll, true); // Use capture to catch all scroll events
    document.addEventListener('scroll', handleScroll, true);

    return () => {
      // Clean up event listeners
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('scroll', handleScroll, true);

      // Destroy Flatpickr instances
      birthDatePicker.destroy();
      adoptionDatePicker.destroy();
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "radio") {
      setFormData((prev) => ({ ...prev, [name]: value }));
    } else if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        interests: checked
          ? [...prev.interests, value]
          : prev.interests.filter((interest) => interest !== value),
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handler for image file input
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          petImagePreview: reader.result,
          petImageFile: file,
        }));
      };
      reader.readAsDataURL(file);
    } else {
      setFormData((prev) => ({
        ...prev,
        petImagePreview: null,
        petImageFile: null,
      }));
    }
  };

  // Handler to auto-capitalize first letter of pet name and other fields
  const handleCapitalizeBlur = (e) => {
    const { name, value } = e.target;
    if (value) {
      const capitalizedValue = value.charAt(0).toUpperCase() + value.slice(1);
      setFormData((prev) => ({ ...prev, [name]: capitalizedValue }));
    }
  };

  const handleSave = async () => {
    // Clear previous errors
    setErrors([]);
    setIsLoading(true);

    try {
      // Validate required fields before sending
      const validationErrors = [];

      if (!formData.petName || formData.petName.trim() === "") {
        validationErrors.push(t("addPet.petNameRequired"));
      }

      if (!formData.species_id || formData.species_id === "") {
        validationErrors.push(t("addPet.speciesRequired"));
      }

      if (!formData.gender || formData.gender === "") {
        validationErrors.push(t("addPet.genderRequired"));
      }

      if (!formData.age || formData.age.trim() === "") {
        validationErrors.push(t("addPet.birthDateRequired"));
      }

      if (!formData.breed || formData.breed === "") {
        validationErrors.push(t("addPet.breedRequired"));
      }

      // Validate adopted/rescued field if user is a Rescuer
      if (isRescuer && (!formData.adoptedRescued || formData.adoptedRescued.trim() === "")) {
        validationErrors.push(t("addPet.adoptedRescuedRequired") || "Please select whether the pet is adopted or rescued");
      }

      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        setIsLoading(false);
        return;
      }

      // Use birth date directly from the date picker
      const birthDate = formData.age || null;

      // Prepare the API payload based on form data
      const apiPayload = {
        name: formData.petName.trim(),
        species_id: formData.species_id,
        breed_id: formData.breed,
        gender: formData.gender,
        adopted_rescued: formData.adoptedRescued || "adopted", // Default to "adopted" if not set
        color: formData.color,
        provider_name: formData.provider,
        provider_number: null, // Optional field - send null if not provided
        policy_number: formData.policyNumber || "",
        birth_date: birthDate,
        adoption_date: formData.adoptionDate || null,
        microchip_id: formData.microchipId || "",
        smart_collar_id: formData.smartCollarId || "",
        weight: parseFloat(formData.weight) || 0,
        size_category: "medium", // Default value as per your specification
        is_spayed_neutered: formData.spayedNeutered === "yes" ? 1 : 0,
        bio: formData.interests.join(", ") || "No bio provided", // Use interests as bio
        special_notes: "", // Default empty value
        allergies: "", // Default empty value
        last_known_location: "Central Park, New York", // Default value as per your specification
        is_location_tracking_enabled: 1, // Default value as per your specification
        is_public_profile: 1, // Default value as per your specification
        allow_social_interactions: 1, // Default value as per your specification
        profile_image: formData.petImageFile || null,
      };

      // Call the API endpoint
      const response = await petAPI.createPet(apiPayload);

      // Set default celebration message and show modal
      setTextPost(
        t("addPet.socialPostTemplate", { name: formData.petName })
      );
      setShowCelebrateModal(true);
      setIsLoading(false);
    } catch (error) {
      console.error("Error saving pet:", error);
      setIsLoading(false);

      // Handle API validation errors
      if (error.response && error.response.data && error.response.data.errors) {
        const apiErrors = error.response.data.errors;
        const errorMessages = Object.values(apiErrors).flat();
        console.error("API Validation Errors:", apiErrors);
        console.error("Formatted Error Messages:", errorMessages);
        setErrors(errorMessages);
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        console.error("API Error Message:", error.response.data.message);
        console.error("Full API Response:", error.response.data);
        setErrors([error.response.data.message]);
      } else {
        console.error("Unknown Error:", error);
        console.error("Error Object:", {
          message: error.message,
          stack: error.stack,
          response: error.response,
        });
        setErrors([t("addPet.genericError")]);
      }
    }
  };

  const handleAddAnother = () => {
    setShowSuccessModal(false);
    // Reset form
    setFormData({
      gender: "",
      adoptedRescued: "adopted", // Default to "adopted"
      petName: "",
      color: "",
      provider: "",
      policyNumber: "",
      species_id: "",
      breed: "",
      spayedNeutered: "",
      microchipId: "",
      smartCollarId: "",
      age: "",
      weight: "",
      adoptionDate: "",
      interests: [],
      petImagePreview: null,
      petImageFile: null,
    });
  };

  const handleReturnToMain = () => {
    setShowSuccessModal(false);
    if (location.state?.fromProfile) {
      navigate(`/social/profile`);
    } else {
      navigate(`/pets`);
    }
  };

  const handleCreatePost = async () => {
    try {
      setIsPosting(true);
      const postData = {
        content: textPost,
        // Use the actual file object for the post image
        image: formData.petImageFile,
      };

      await postsAPI.createPost(postData);
      setShowCelebrateModal(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Error creating celebration post:", error);
      // Even if post fails, we show success for the pet addition itself
      setShowCelebrateModal(false);
      setShowSuccessModal(true);
    } finally {
      setIsPosting(false);
    }
  };

  const handleMaybeLater = () => {
    setShowCelebrateModal(false);
    setShowSuccessModal(true);
  };

  const handleCancel = () => {
    if (location.state?.fromProfile) {
      navigate(`/social/profile`);
    } else {
      navigate(`/dashboard/pets`);
    }
  };

  const petInterests = [
    {
      name: t("addPet.interestsList.medicalCare"),
      value: "Medical Care",
      imageSrc: "/icons/pets/breeding.svg",
    },
    {
      name: t("addPet.interestsList.grooming"),
      value: "Grooming",
      imageSrc: "/icons/pets/grooming.svg",
    },
    {
      name: t("addPet.interestsList.trainingActivities"),
      value: "Training & Activities",
      imageSrc: "/icons/pets/competing.svg",
    },
    {
      name: t("addPet.interestsList.boardingDaycare"),
      value: "Boarding / Day care",
      imageSrc: "/icons/pets/gathering.svg",
    },
    {
      name: t("addPet.interestsList.socializing"),
      value: "Socializing",
      imageSrc: "/icons/pets/socializing.svg",
    },
    {
      name: t("addPet.interestsList.onlineConsultation"),
      value: "Online Consultation / Records",
      imageSrc: "/icons/pets/tech-needs.svg",
    },
  ];

  return (
    <div className="addpet-root" dir={i18n.dir()} lang={i18n.language}>
      <Navbar userType={userType} />

      <main className={`addpet-main ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="addpet-container">
          {/* Header Section with Back Button */}
          <div className="addpet-header">
            <button
              onClick={() => {
                if (location.state?.fromProfile) {
                  navigate(`/social/profile`);
                } else {
                  navigate(`/pets`);
                }
              }}
              className="addpet-back-btn"
              style={{
                '--user-type-color': userTypeColor,
                flexDirection: isRTL ? 'row-reverse' : 'row'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              {t("addPet.back")}
            </button>
            <div className="addpet-header-content">
              <h1 className="addpet-title">
                {t("addPet.pageTitle")}
              </h1>

            </div>
          </div>

          {/* Enhanced Profile Picture Upload Section */}
          <div className="addpet-image-section" style={{ '--user-type-color': userTypeColor }}>
            <div className="addpet-image-container">
              <div className="addpet-image-wrapper">
                <div
                  className="addpet-image-upload"
                  onClick={() => document.getElementById("pet-image-upload").click()}
                >
                  {formData.petImagePreview ? (
                    <img
                      src={formData.petImagePreview || "/placeholder.svg"}
                      alt={t("addPet.photo.previewAlt")}
                      className="addpet-preview-image"
                    />
                  ) : (
                    <div className="addpet-upload-placeholder">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7,10 12,15 17,10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      <span className="addpet-upload-text">{t("addPet.photo.uploadLabel")}</span>
                    </div>
                  )}
                  <div className="addpet-upload-overlay">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      {formData.petImagePreview ? (
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                      ) : (
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      )}
                      {formData.petImagePreview ? (
                        <path d="M12 20h9" />
                      ) : (
                        <>
                          <polyline points="7,10 12,15 17,10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </>
                      )}
                    </svg>
                    <span>{formData.petImagePreview ? t("addPet.photo.changeLabel") : t("addPet.photo.uploadLabel")}</span>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="addpet-hidden-input"
                    id="pet-image-upload"
                  />
                </div>
                <div className="addpet-image-info">
                  <h3 className="addpet-image-title">{t("addPet.photo.title")}</h3>
                  <p className="addpet-image-description">
                    {t("addPet.photo.description")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Basic Information Section */}
          <section className="addpet-form-section" style={{ '--user-type-color': userTypeColor }}>
            <div className="addpet-section-header">
              <h2 className="addpet-section-title">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                {t("addPet.basicInformation")}
              </h2>
            </div>
            <div className="addpet-form-content">

              <div className="addpet-grid-2">
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.petName")} <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="petName"
                    value={formData.petName}
                    onChange={handleInputChange}
                    onBlur={handleCapitalizeBlur} // Applied auto-capitalization
                    placeholder={t("addPet.petNamePlaceholder")}
                    className="addpet-input"
                  />
                </div>
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.color")} <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
                  </label>
                  <div className="addpet-select-wrapper">
                    <select
                      name="color"
                      value={formData.color}
                      onChange={handleInputChange}
                      className="addpet-input addpet-select"
                    >
                      <option value="" disabled>
                        {t("addPet.colorPlaceholder")}
                      </option>
                      <option value="Black">{t("addPet.colors.black")}</option>
                      <option value="White">{t("addPet.colors.white")}</option>
                      <option value="Brown">{t("addPet.colors.brown")}</option>
                      <option value="Golden">{t("addPet.colors.golden")}</option>
                      <option value="Gray">{t("addPet.colors.gray")}</option>
                      <option value="Orange">{t("addPet.colors.orange")}</option>
                      <option value="Cream">{t("addPet.colors.cream")}</option>
                      <option value="Tan">{t("addPet.colors.tan")}</option>
                      <option value="Mixed">{t("addPet.colors.mixed")}</option>
                      <option value="Other">{t("addPet.colors.other")}</option>
                    </select>
                    <div className="addpet-select-arrow">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#6B7280"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.species")} <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <div className="addpet-select-wrapper">
                    <select
                      name="species"
                      value={formData.species_id || ""}
                      onChange={(e) => {
                        setSelectedSpecies(e.target.value);
                        setFormData((prev) => ({
                          ...prev,
                          species_id: e.target.value,
                          breed: "",
                        }));
                      }}
                      className="addpet-input addpet-select"
                    >
                      <option value="" disabled>
                        {t("addPet.speciesPlaceholder")}
                      </option>
                      {speciesList.map((species) => (
                        <option key={species.id} value={species.id}>
                          {getLocalizedName(species)}
                        </option>
                      ))}
                    </select>
                    <div className="addpet-select-arrow">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#6B7280"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.breed")} <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <div className="addpet-select-wrapper">
                    <select
                      name="breed"
                      value={formData.breed || ""}
                      onChange={handleInputChange}
                      className="addpet-input addpet-select"
                    >
                      <option value="" disabled>
                        {t("addPet.breedPlaceholder")}
                      </option>
                      {(breedList || []).map((breed) => (
                        <option key={breed.id} value={breed.id}>
                          {getLocalizedName(breed)}
                        </option>
                      ))}
                    </select>
                    <div className="addpet-select-arrow">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#6B7280"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="addpet-grid-2">
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.gender")} <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <div className="addpet-radio-group">
                    <label className="addpet-radio-item">
                      <input
                        type="radio"
                        name="gender"
                        value="female"
                        checked={formData.gender === "female"}
                        onChange={handleInputChange}
                        className="addpet-radio"
                      />
                      <span className="addpet-radio-text">
                        {t("addPet.female")}
                      </span>
                    </label>
                    <label className="addpet-radio-item">
                      <input
                        type="radio"
                        name="gender"
                        value="male"
                        checked={formData.gender === "male"}
                        onChange={handleInputChange}
                        className="addpet-radio"
                      />
                      <span className="addpet-radio-text">
                        {t("addPet.male")}
                      </span>
                    </label>
                  </div>
                </div>
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.adoptedRescued")}
                    {isRescuer ? (
                      <span style={{ color: '#EF4444' }}>*</span>
                    ) : (
                      <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
                    )}
                  </label>
                  <div className="addpet-radio-group">
                    <label className="addpet-radio-item">
                      <input
                        type="radio"
                        name="adoptedRescued"
                        value="adopted"
                        checked={formData.adoptedRescued === "adopted"}
                        onChange={handleInputChange}
                        className="addpet-radio"
                      />
                      <span className="addpet-radio-text">
                        {t("addPet.adopted")}
                      </span>
                    </label>
                    <label className="addpet-radio-item">
                      <input
                        type="radio"
                        name="adoptedRescued"
                        value="rescued"
                        checked={formData.adoptedRescued === "rescued"}
                        onChange={handleInputChange}
                        className="addpet-radio"
                      />
                      <span className="addpet-radio-text">
                        {t("addPet.rescued")}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Physical Information Section */}
          <section className="addpet-form-section" style={{ '--user-type-color': userTypeColor }}>
            <div className="addpet-section-header">
              <h2 className="addpet-section-title">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
                {t("addPet.physicalInformation")}
              </h2>
            </div>
            <div className="addpet-form-content">
              <div className="addpet-mb-30">
                <label className="addpet-label">
                  {t("addPet.spayedNeutered")} <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
                </label>
                <div className="addpet-radio-group">
                  <label className="addpet-radio-item">
                    <input
                      type="radio"
                      name="spayedNeutered"
                      value="yes"
                      checked={formData.spayedNeutered === "yes"}
                      onChange={handleInputChange}
                      className="addpet-radio"
                    />
                    <span className="addpet-radio-text">
                      {t("addPet.yes")}
                    </span>
                  </label>
                  <label className="addpet-radio-item">
                    <input
                      type="radio"
                      name="spayedNeutered"
                      value="no"
                      checked={formData.spayedNeutered === "no"}
                      onChange={handleInputChange}
                      className="addpet-radio"
                    />
                    <span className="addpet-radio-text">
                      {t("addPet.no")}
                    </span>
                  </label>
                </div>
              </div>
              <div className="addpet-grid-2">
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.birthDate")} <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <div className="addpet-relative-full" style={{ '--user-type-color': userTypeColor }}>
                    <input
                      type="text"
                      name="age"
                      id="birth-date-input"
                      value={formData.age}
                      onChange={handleInputChange}
                      className="addpet-input addpet-select"
                      placeholder={t("addPet.birthDatePlaceholder")}
                      readOnly
                    />
                    <div className="addpet-select-arrow">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#6B7280"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.weight")} <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
                  </label>
                  <input
                    type="text"
                    name="weight"
                    value={formData.weight}
                    onChange={handleInputChange}
                    placeholder={t("addPet.weightPlaceholder")}
                    className="addpet-input"
                  />
                </div>
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.adoptionDate")} <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
                  </label>
                  <div className="addpet-select-wrapper" style={{ '--user-type-color': userTypeColor }}>
                    <input
                      type="text"
                      name="adoptionDate"
                      id="adoption-date-input"
                      value={formData.adoptionDate}
                      onChange={handleInputChange}
                      className="addpet-input addpet-select"
                      placeholder={t("addPet.adoptionDatePlaceholder")}
                      readOnly
                    />
                    <div className="addpet-select-arrow">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#6B7280"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.microchipId")} <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
                  </label>
                  <input
                    type="text"
                    name="microchipId"
                    value={formData.microchipId}
                    onChange={handleInputChange}
                    placeholder={t("addPet.microchipPlaceholder")}
                    className="addpet-input"
                  />
                </div>
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.smartCollarId")} <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
                  </label>
                  <input
                    type="text"
                    name="smartCollarId"
                    value={formData.smartCollarId}
                    onChange={handleInputChange}
                    placeholder={t("addPet.smartCollarPlaceholder")}
                    className="addpet-input"
                  />
                </div>
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.provider")} <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
                  </label>
                  <input
                    type="text"
                    name="provider"
                    value={formData.provider}
                    onChange={handleInputChange}
                    onBlur={handleCapitalizeBlur} // Applied auto-capitalization
                    placeholder={t("addPet.providerPlaceholder")}
                    className="addpet-input"
                  />
                </div>
                <div className="addpet-control">
                  <label className="addpet-label">
                    {t("addPet.policyNumber")} <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
                  </label>
                  <input
                    type="text"
                    name="policyNumber"
                    value={formData.policyNumber}
                    onChange={handleInputChange}
                    onBlur={handleCapitalizeBlur} // Applied auto-capitalization
                    placeholder={t("addPet.policyNumberPlaceholder")}
                    className="addpet-input"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Pet Interests Section */}
          <section className="addpet-form-section" style={{ '--user-type-color': userTypeColor }}>
            <div className="addpet-section-header">
              <h2 className="addpet-section-title">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                {t("addPet.interests")}
              </h2>
            </div>
            <div className="addpet-form-content">
              <div className="addpet-interests">
                {petInterests.map((interest) => (
                  <div
                    key={interest.value || interest.name}
                    className={`addpet-interest-card ${formData.interests.includes(interest.value || interest.name) ? 'selected' : ''}`}
                    onClick={() => {
                      if (formData.interests.includes(interest.value || interest.name)) {
                        setFormData((prev) => ({
                          ...prev,
                          interests: prev.interests.filter(
                            (item) => item !== (interest.value || interest.name)
                          ),
                        }))
                      } else {
                        setFormData((prev) => ({
                          ...prev,
                          interests: [...prev.interests, (interest.value || interest.name)],
                        }))
                      }
                    }}
                  >
                    <div className="addpet-interest-icon">
                      <img
                        src={interest.imageSrc || "/placeholder.svg"}
                        alt={interest.name}
                        width={32}
                        height={32}
                      />
                    </div>
                    <div className="addpet-interest-content">
                      <span className="addpet-interest-text">
                        {interest.name}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Error Display */}
          {errors.length > 0 && (
            <div className="addpet-error-box">
              <div className="addpet-error-title">
                {t("addPet.errorTitle")}
              </div>
              <ul className="addpet-error-list">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Buttons */}
          <div className="addpet-actions">
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="addpet-btn-primary"
            >
              {isLoading ? t("addPet.saving") : t("addPet.save")}
            </button>
            <button
              onClick={handleCancel}
              className="addpet-btn-secondary"
            >
              {t("addPet.cancel")}
            </button>
          </div>
        </div>
      </main>

      <Footer />

      {/* Modals */}
      {(showCelebrateModal || showSuccessModal) && (
        <Confetti width={width} height={height} className="confetti-overlay" />
      )}
      <AddAnotherReturnModal
        isOpen={showSuccessModal}
        onAddAnother={handleAddAnother}
        onReturnToMain={handleReturnToMain}
        userTypeColor={userTypeColor}
      />
      <CelebrateModal
        isOpen={showCelebrateModal}
        onPost={handleCreatePost}
        onMaybeLater={handleMaybeLater}
        petImagePreview={formData.petImagePreview}
        petName={formData.petName}
        setTextPost={setTextPost}
        textPost={textPost}
        isPosting={isPosting}
        userTypeColor={userTypeColor}
      />
    </div>
  );
};

export default AddPet;
